import { http } from '@/lib/apiClient';
import type { WorkflowDocumentType, WorkflowStepType } from '@/api/workflows.api';

export interface DashboardKpis {
  activeVendors: number;
  pendingApprovals: number;
  kycAttention: number;
  sapSyncHealthPercent: number;
}

export interface DashboardApprovalItem {
  taskId: string;
  instanceId: string;
  documentType: WorkflowDocumentType;
  documentId: string;
  documentNo: string | null;
  title: string;
  stepNo: number;
  stepType: WorkflowStepType;
  assignedRoleCode: string;
  createdAt: string;
}

export interface DashboardActivityItem {
  id: number;
  title: string;
  summary: string | null;
  entityType: string;
  entityId: string;
  entityReference: string | null;
  action: string;
  severity: string;
  performedBy: string | null;
  performedByName: string | null;
  performedAt: string;
}

export type DashboardSummaryCardCode =
  | 'ACTIVE_VENDORS'
  | 'PENDING_APPROVALS'
  | 'KYC_ATTENTION'
  | 'OPEN_PURCHASE_ORDERS'
  | 'PENDING_GRNS'
  | 'PENDING_INVOICES'
  | 'PENDING_PAYMENTS';

export interface DashboardSummaryCard {
  code: DashboardSummaryCardCode;
  label: string;
  group: string;
  value: number;
  unit: string | null;
  severity: string;
  filterKey: string | null;
  filterValues: string[];
  statusLabels: string[];
}

export type DashboardStatusGroupCode = 'VENDOR' | 'PURCHASE_ORDER' | 'GOODS_RECEIPT' | 'VENDOR_INVOICE' | 'PAYMENT';
export interface DashboardStatusCount { status: string; label?: string | null; count: number }
export interface DashboardStatusGroup {
  group: DashboardStatusGroupCode;
  status?: string;
  label?: string | null;
  count?: number;
  items?: DashboardStatusCount[];
  statuses?: DashboardStatusCount[];
  counts?: Record<string, number>;
}

export interface DashboardNotificationItem {
  id: string;
  title?: string | null;
  subject?: string | null;
  summary?: string | null;
  body?: string | null;
  severity?: string | null;
  relatedEntityType?: string | null;
  relatedEntityId?: string | null;
  entityReference?: string | null;
  isRead?: boolean;
  readAt?: string | null;
  createdAt: string;
}

export interface DashboardNotificationPreview {
  unreadCount: number;
  items: DashboardNotificationItem[];
}

export interface SapSyncHealth {
  healthPercent: number;
  pendingOutbox: number;
  failedOutbox: number;
  successfulLogs24h: number;
  failedLogs24h: number;
}

export interface DashboardOverview {
  kpis: DashboardKpis;
  summaryCards: DashboardSummaryCard[];
  statusBreakdown: DashboardStatusGroup[] | Record<string, DashboardStatusCount[] | Record<string, number>>;
  approvalQueue: DashboardApprovalItem[];
  recentActivity: DashboardActivityItem[];
  notificationPreview: DashboardNotificationPreview;
  sapSyncHealth: SapSyncHealth;
}

export const dashboardApi = {
  overview: () => http.get<DashboardOverview>('/api/v1/dashboard/overview'),
};
